import React, { useState, useEffect } from 'react';
import { X, Link as LinkIcon, Copy, Check, Mail, Calendar } from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface ClientLinkModalProps {
  applicationId: number;
  onClose: () => void;
}

export const ClientLinkModal: React.FC<ClientLinkModalProps> = ({ applicationId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [linkData, setLinkData] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState<number>(30);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadExistingLink();
  }, [applicationId]);

  const loadExistingLink = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/crm/applications/${applicationId}/token`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.hasToken) {
        setLinkData(response.data);
      }
    } catch (err: any) {
      console.error('Error loading token:', err);
      if (err.response?.status !== 404) {
        setError('Nie udało się załadować linku');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    setGenerating(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/crm/applications/${applicationId}/generate-token`,
        { expiresInDays },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setLinkData(response.data);
      }
    } catch (err: any) {
      console.error('Error generating token:', err);
      setError(err.response?.data?.error || 'Nie udało się wygenerować linku');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = () => {
    if (linkData?.portalUrl) {
      navigator.clipboard.writeText(linkData.portalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Bezterminowy';
    return new Date(dateString).toLocaleString('pl-PL');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <LinkIcon className="mr-3" size={28} />
              <div>
                <h2 className="text-2xl font-bold">Link dla Klienta</h2>
                <p className="text-blue-100 text-sm mt-1">
                  Portal do uploadowania dokumentów przez klienta
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Ładowanie...</p>
            </div>
          ) : linkData ? (
            <>
              {/* Existing Link Display */}
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Check className="text-green-600 mr-2" size={20} />
                  <h3 className="font-bold text-green-900">Link wygenerowany</h3>
                </div>

                <div className="bg-white rounded p-3 mb-3 break-all font-mono text-sm">
                  {linkData.portalUrl}
                </div>

                <button
                  onClick={handleCopyLink}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="mr-2" size={18} />
                      Skopiowano!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2" size={18} />
                      Kopiuj link
                    </>
                  )}
                </button>
              </div>

              {/* Link Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center text-gray-600 mb-1">
                    <Calendar size={16} className="mr-2" />
                    <span className="font-semibold">Wygasa</span>
                  </div>
                  <p className="text-gray-900">{formatDate(linkData.expiresAt)}</p>
                </div>

                {linkData.lastUsedAt && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-gray-600 mb-1 font-semibold">
                      Ostatnio użyty
                    </div>
                    <p className="text-gray-900">{formatDate(linkData.lastUsedAt)}</p>
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-bold text-blue-900 mb-2">📋 Instrukcje dla klienta:</h4>
                <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                  <li>Wyślij klientówi link (email, SMS, WhatsApp)</li>
                  <li>Klient otwórzy link i zobaczy swój wniosek</li>
                  <li>Klient może uploadować dokumenty z wybraną kategorią</li>
                  <li>Dokumenty trafiają automatycznie na Google Drive</li>
                  <li>Klient widzi status wniosku i komunikaty</li>
                </ol>
              </div>

              {/* Regenerate */}
              <div className="border-t pt-4">
                <p className="text-sm text-gray-600 mb-3">
                  Jeśli chcesz wygenerować nowy link (np. zmieńić datę wygaśnięcia):
                </p>
                <button
                  onClick={handleGenerateLink}
                  disabled={generating}
                  className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-700 disabled:bg-gray-400"
                >
                  {generating ? 'Generowanie...' : 'Wygeneruj nowy link'}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Generate New Link */}
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Wygeneruj link dla klienta</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Link umożliwi klientówi samodzielne przesyłanie dokumentów bez logowania do systemu.
                </p>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Link wygasa po (dni):
                  </label>
                  <select
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(parseInt(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={7}>7 dni</option>
                    <option value={14}>14 dni</option>
                    <option value={30}>30 dni (domyślnie)</option>
                    <option value={60}>60 dni</option>
                    <option value={90}>90 dni</option>
                    <option value={0}>Bezterminowy</option>
                  </select>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleGenerateLink}
                  disabled={generating}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 disabled:bg-gray-400 flex items-center justify-center transition-colors"
                >
                  {generating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Generowanie...
                    </>
                  ) : (
                    <>
                      <LinkIcon className="mr-2" size={20} />
                      Wygeneruj link
                    </>
                  )}
                </button>
              </div>

              {/* Info Box */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">ℹ️ Co może klient?</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>✅ Przeglądać swój wniosek</li>
                  <li>✅ Uploadować dokumenty z kategoriami</li>
                  <li>✅ Widzieć status wniosku</li>
                  <li>✅ Czytać komunikaty od operatora</li>
                  <li>✅ Pobierać przesłane dokumenty</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
